/**
 * Pre-i18n stop-gap for the safety domain. Until the app gains a real
 * i18n library, the backend keeps emitting English strings (the canonical
 * record) and the UI translates the structured pieces (status, blocking
 * layer, category enum) to PT-BR for display.
 *
 * Free-form ``reason`` text from the LLM judge is shown verbatim — there is no
 * useful PT mapping for arbitrary sentences here. Once react-i18next or
 * equivalent lands, drop this file and source labels from locale files.
 */

import type { SafetyVerdictOut } from '@/api/endpoints/safety'

const STATUS_PT: Record<string, string> = {
  passed: 'Seguro',
  blocked: 'Bloqueado',
  needs_review: 'Aguardando revisão',
  pending: 'Pendente',
  error: 'Erro',
  never_checked: 'Sem scan',
}

/** Short layer label — one or two words, used inside chips and table cells.
 * Keys mirror :class:`SafetyLayer` in ``backend/src/db/models/_base.py``. */
const LAYER_PT: Record<string, string> = {
  blocklist: 'Lista de bloqueio',
  llm_judge: 'Juiz LLM',
  llm_judge_clips: 'Juiz LLM (clips)',
}

/**
 * Friendly title for each layer card. Spelled out so the user doesn't have
 * to know what "llm_judge" means.
 */
const LAYER_TITLE_PT: Record<string, string> = {
  blocklist: 'Lista de bloqueio',
  llm_judge: 'Juiz LLM',
  llm_judge_clips: 'Juiz LLM — clips',
}

/**
 * One-sentence description of what each layer actually checks. Shown under
 * the layer title so the reviewer knows why a hit there matters.
 */
const LAYER_DESCRIPTION_PT: Record<string, string> = {
  blocklist:
    'Procura palavras e expressões proibidas no roteiro (regex rápido, sem IA).',
  llm_judge:
    'O juiz LLM lê o roteiro (e os frames, no scan pós-render) e decide se há associação ofensiva.',
  llm_judge_clips:
    'O juiz LLM valida cada clip de vídeo individualmente antes da composição (estágio pre_compose).',
}

/**
 * Mirrors `SafetyCategory` in `backend/src/db/models/_base.py`. Keep in sync —
 * the enum is the contract; new values should land here in the same PR.
 */
const CATEGORY_PT: Record<string, string> = {
  racism: 'Racismo',
  homophobia: 'Homofobia',
  transphobia: 'Transfobia',
  misogyny: 'Misoginia',
  xenophobia: 'Xenofobia',
  antisemitism_or_islamophobia: 'Antissemitismo / Islamofobia',
  ableism: 'Capacitismo',
  harassment: 'Assédio',
  violence_incitement: 'Incitação à violência',
  extremism_or_hate_symbols: 'Extremismo / Símbolos de ódio',
  sexual_content_minors: 'Conteúdo sexual envolvendo menores',
  offensive_association: 'Associação ofensiva',
  other: 'Outro',
}

export function translateStatus(status: string): string {
  return STATUS_PT[status] ?? status
}

export function translateLayer(layer: string | null | undefined): string {
  if (!layer) return '—'
  return LAYER_PT[layer] ?? layer
}

export function layerTitle(layer: string): string {
  return LAYER_TITLE_PT[layer] ?? LAYER_PT[layer] ?? layer
}

export function layerDescription(layer: string): string {
  return LAYER_DESCRIPTION_PT[layer] ?? ''
}

export function translateCategory(category: string): string {
  return CATEGORY_PT[category] ?? category
}

export function translateCategories(categories: string[]): string[] {
  return categories.map(translateCategory)
}

/** Per-layer outcome rendered as a short PT sentence the user can scan. */
export type LayerOutcome = 'passed' | 'blocked' | 'errored' | 'not_run'

export function layerOutcomeLabel(outcome: LayerOutcome): string {
  switch (outcome) {
    case 'passed':
      return 'Passou'
    case 'blocked':
      return 'Bloqueou'
    case 'errored':
      return 'Erro'
    case 'not_run':
      return 'Não rodou'
  }
}

/**
 * Best-effort PT version of a verdict's reason. The freeform sentence stays
 * in English (the LLM emits it that way and we want to preserve the original
 * wording for future i18n); we prepend a structured PT summary built from
 * the enum fields so a Portuguese-speaking reviewer can grasp the verdict
 * without having to read the English sentence first.
 */
export function buildVerdictSummaryPt(verdict: SafetyVerdictOut): string {
  if (verdict.status === 'passed') {
    return 'Tudo certo: nenhuma camada bloqueou este vídeo.'
  }
  if (verdict.status === 'blocked' && verdict.blocking_layer) {
    return `Bloqueado pela camada ${layerTitle(verdict.blocking_layer)}.`
  }
  if (verdict.status === 'needs_review') {
    return 'O scan automático passou, mas o tema pediu revisão humana antes de publicar.'
  }
  if (verdict.status === 'error') {
    return 'O scan não terminou. Rode um rescan para tentar de novo.'
  }
  if (verdict.status === 'pending') {
    return 'Scan em andamento.'
  }
  if (verdict.status === 'never_checked') {
    return 'Este vídeo ainda não passou pelo scan de segurança.'
  }
  return translateStatus(verdict.status)
}
